import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { queryClient } from '@/lib/query-client';
import { isImportRunning } from '@/modules/accounts/pulse-import';
import { pulseImportService } from '@/services/pulse-onboarding.service';
import type { PulseImport } from '@/types';

/** How often the history is re-read while an import is still running. */
export const PULSE_IMPORT_POLL_MS = 10_000;

export const pulseImportsQueryKey = (accountId: number | null) => ['pulse-imports', accountId];

/** Poll only while something is running, so a settled history costs nothing. */
export const pulseImportsRefetchInterval = (imports: PulseImport[] | undefined): number | false =>
  (imports ?? []).some(isImportRunning) ? PULSE_IMPORT_POLL_MS : false;

const importsQueryAtom = atomWithQuery((get) => {
  const accountId = get(selectedAccountIdAtom);
  return {
    queryKey: pulseImportsQueryKey(accountId),
    enabled: accountId != null,
    queryFn: () => pulseImportService.list(accountId as number),
    refetchInterval: (query: { state: { data?: PulseImport[] } }) =>
      pulseImportsRefetchInterval(query.state.data)
  };
});

/** The selected account's Pulse imports, newest first, followed while one runs. */
export const usePulseImports = () => {
  const [result] = useAtom(importsQueryAtom);
  return result;
};

export const refreshPulseImports = (accountId: number | null) =>
  queryClient.invalidateQueries({ queryKey: pulseImportsQueryKey(accountId) });
